import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { Table } from '@/components/Table'
import { Spinner } from '@/components/ui/spinner'
import { useDocumentStore } from '@/features/document'
import {
  createDocumentTOCColumns,
  TflMetaWithRowNumber,
} from '@/features/view-tfl-uploaded/columns'
import { ViewerSurface } from '@/features/view-tfl-uploaded/components'
import { useTflViewerStore } from '@/features/view-tfl-uploaded/store'
import { compareDocs } from '@/features/view-tfl-uploaded/utils/parser'

export function DocumentTOC() {
  const { t } = useTranslation()
  const { id: deliverableSetId } = useParams<{ id: string }>()

  const {
    isLoadingTOC,
    tocError,
    openDocuments,
    setOpenDocument,
    setActiveDocumentId,
  } = useDocumentStore()

  const { metadataByDocId } = useTflViewerStore()

  const itemsWithRowNumbers = useMemo(
    () =>
      Object.values(metadataByDocId)
        ?.filter((i) => i !== undefined)
        ?.filter((i) => i.dsId === deliverableSetId)
        ?.sort(compareDocs)
        ?.map((item, index) => ({ ...item, rowNumber: index + 1 })),
    [metadataByDocId, deliverableSetId]
  )

  const columns = useMemo(() => createDocumentTOCColumns(t), [t])

  const handleRowClick = (row: TflMetaWithRowNumber) => {
    if (!deliverableSetId || !row.id) return
    const isOpening = openDocuments.some((doc) => doc.id === row.id)
    if (!isOpening) {
      setOpenDocument([
        ...openDocuments,
        { id: row.id, filename: row.displayName || '' },
      ])
    }
    setActiveDocumentId(row.id)
  }

  return (
    <ViewerSurface className="flex-1 min-h-0">
      <div className="px-6 py-5 border-b border-gray-200">
        <span className="text-2xl font-semibold text-gray-900">
          {t('tflViewer.documentTOC.title')}
        </span>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoadingTOC && (
          <div className="flex items-center justify-center p-8">
            <Spinner />
          </div>
        )}

        {tocError && (
          <div
            className="flex flex-col items-center justify-center p-8"
            role="alert"
          >
            <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
              <h3 className="mb-2 text-lg font-semibold text-red-900">
                Failed to load table of contents
              </h3>
              <p className="mb-4 text-sm text-red-700">
                {tocError.message || 'Please try again later.'}
              </p>
            </div>
          </div>
        )}

        {!isLoadingTOC && !tocError && itemsWithRowNumbers.length > 0 && (
          <Table
            className="h-full"
            fullHeight
            columns={columns}
            data={itemsWithRowNumbers}
            onRowClick={handleRowClick}
            roundedTop={false}
            roundedBottom={false}
            noDataMessage={t('tflViewer.documentTOC.noDocuments')}
          />
        )}

        {!isLoadingTOC && !tocError && itemsWithRowNumbers.length === 0 && (
          <div className="flex flex-col items-center justify-center p-8 text-gray-500">
            <p className="text-sm">No documents found.</p>
          </div>
        )}
      </div>
    </ViewerSurface>
  )
}
